import { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { Button, Flex, Table } from "antd"
import { PhoneOutlined, MailOutlined, UserOutlined, CloseOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"
import CustomPagination from "components/common/CustomPagination"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import { guideBookingStatus } from "store/bookings"
import TicketTypeIcon from "../TicketTypeIcon"
import ReservationFilterModal from "./ReservationFilterModal"
import SendMessageButton from "../../SendMessageButton"
import PaymentTag from "../../PaymentTag"
import BookingTag from "../../BookingTag"
import { Text } from "ui/Typography"
import { checkInGuests, checkOutGuests } from "store/reservations"
import CheckInBadge from "../../CheckIn/CheckInBadge/CheckInBadge"
import CheckInGuestsModal from "../../CheckIn/CheckInGuestsModal/CheckInGuestsModal"
import CheckOutGuestsModal from "../../CheckIn/CheckOutGuestsModal/CheckOutGuestsModal"
import IncompleteDocumentsModal from "../../CheckIn/IncompleteDocumentsModal/IncompleteDocumentsModal"
import { deriveCheckInState } from "../../CheckIn/checkInUtils"
import { useCheckInFlow } from "../../CheckIn/useCheckInFlow"
import { PAYMENT_CHIP_LABELS, EMPTY_FILTERS } from "./constants"
import styles from "./index.module.css"

const FilterChip = ({ label, onRemove }) => (
  <Flex align="center" gap={4} className={styles.filterChip}>
    <span className={styles.filterChipLabel}>{label}</span>
    <button type="button" className={styles.filterChipRemove} onClick={onRemove} aria-label={`Remove ${label} filter`}>
      <CloseOutlined />
    </button>
  </Flex>
)

FilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
}

const WaiverCell = ({ docsSign, docsTotal }) => (
  <BookingTag color={docsSign === docsTotal ? "success" : "error"}>
    <DSIcon name="file" width={14} height={14} fill="currentColor" />
    {docsSign}/{docsTotal}
  </BookingTag>
)

WaiverCell.propTypes = {
  docsSign: PropTypes.number.isRequired,
  docsTotal: PropTypes.number.isRequired
}

const buildActiveChips = (filters, experienceLabelMap) => {
  const chips = []
  filters.experiences?.forEach((exp) => chips.push({ key: `exp:${exp}`, label: experienceLabelMap.get(exp) ?? exp }))
  if (filters.payment && PAYMENT_CHIP_LABELS[filters.payment]) {
    chips.push({ key: "payment", label: PAYMENT_CHIP_LABELS[filters.payment] })
  }
  if (filters.waivers === "signed") chips.push({ key: "waivers", label: "Signed" })
  if (filters.waivers === "not_signed") chips.push({ key: "waivers", label: "Not Signed" })
  if (filters.checkIn === "not_checked_in") chips.push({ key: "checkIn", label: "Not checked-in" })
  if (filters.checkIn === "checked_in") chips.push({ key: "checkIn", label: "Checked-in" })
  if (filters.checkIn === "checked_out") chips.push({ key: "checkIn", label: "Checked-out" })
  return chips
}


const ReservationListingTable = ({
  reservations = [],
  loading = false,
  status,
  onRefresh,
  metadata,
  page = 1,
  pageSize = 15,
  onPageChange,
  onPageSizeChange,
  sortKey,
  sortOrder,
  onSortChange,
  onViewBooking,
  onOpenFilters,
  filters = EMPTY_FILTERS,
  onFiltersChange,
  experienceOptions = []
}) => {
  const dispatch = useDispatch()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  // Accept/Decline confirmation for the Requests tab: { record, action: "accept" | "decline" }
  const [confirmTarget, setConfirmTarget] = useState(null)
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const isRequestsTab = status === "requested"
  const checkIn = useCheckInFlow(checkInGuests, checkOutGuests)
  const { getCheckedInCount, getCheckedOutCount, getGuestTypes } = checkIn

  const openFilterModal = () => setFilterModalOpen(true)
  if (onOpenFilters) onOpenFilters.current = openFilterModal

  const data = useMemo(
    () => reservations.map((reservation) => {
      const checkedInCount = getCheckedInCount(reservation)
      const checkedOutCount = getCheckedOutCount(reservation)
      return {
        ...reservation,
        key: reservation.id,
        checkedInCount,
        checkedOutCount,
        // override-aware breakdown so the modals reflect optimistic check-in/out
        guestTypes: getGuestTypes(reservation),
        checkInState: deriveCheckInState(checkedInCount, reservation.guestCount, checkedOutCount)
      }
    }),
    [reservations, getCheckedInCount, getCheckedOutCount, getGuestTypes]
  )

  // Sorting is backend-driven: the parent forwards sort_by/order to the reservations
  // fetch, so we only reflect the active column's direction here.
  const columnSortOrder = (columnKey) => {
    if (sortKey !== columnKey || !sortOrder) return null
    return sortOrder === "asc" ? "ascend" : "descend"
  }

  const experienceLabelMap = useMemo(
    () => new Map(experienceOptions.map((option) => [option.value, option.label])),
    [experienceOptions]
  )

  const activeChips = buildActiveChips(filters, experienceLabelMap)

  const selectedReservations = useMemo(
    () => data.filter((reservation) => selectedRowKeys.includes(reservation.key)),
    [data, selectedRowKeys]
  )

  // Drop any selected keys that no longer exist in the current data (e.g. after switching
  // month/tab/page/filters). Without this the selection bar lingers with stale, unchecked
  // rows — the checkboxes clear but `selectedRowKeys.length` still shows "N selected".
  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const validKeys = new Set(data.map((reservation) => reservation.key))
      const pruned = prev.filter((key) => validKeys.has(key))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [data])

  const removeChip = (key) => {
    if (key.startsWith("exp:")) {
      const exp = Number(key.slice(4))
      onFiltersChange?.((prev) => ({ ...prev, experiences: prev.experiences.filter((experience) => experience !== exp) }))
    } else {
      onFiltersChange?.((prev) => ({ ...prev, [key]: null }))
    }
  }

  const applyFilters = (newFilters) => {
    onFiltersChange?.(newFilters)
    setFilterModalOpen(false)
  }

  const clearAllFilters = () => {
    onFiltersChange?.((prev) => ({ ...EMPTY_FILTERS, experiences: prev.experiences }))
  }

  const openRequestConfirm = (record, action) => setConfirmTarget({ record, action })
  const closeRequestConfirm = () => setConfirmTarget(null)

  // Accept → "booked", Decline → "canceled" (same status_action endpoint the old listing used).
  // Refetch afterwards so the accepted/declined request drops off the Requests tab.
  const handleConfirmRequest = async () => {
    if (!confirmTarget || isSubmittingRequest) return
    setIsSubmittingRequest(true)
    try {
      await dispatch(guideBookingStatus(confirmTarget.record.id, confirmTarget.action === "accept" ? "booked" : "canceled"))
      closeRequestConfirm()
      onRefresh?.()
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const columns = [
    {
      key: "inOut",
      title: "In/Out",
      width: 80,
      render: (_, record) => (
        <CheckInBadge
          state={record.checkInState}
          checkedInCount={record.checkedInCount}
          checkedOutCount={record.checkedOutCount}
          totalGuests={record.guestCount}
          onCheckIn={() => checkIn.openCheckIn(record, record.waiversComplete)}
          onCheckOut={() => checkIn.openCheckOut(record)}
          onReset={() => checkIn.reset(record)}
        />
      )
    },
    {
      key: "client",
      dataIndex: "client",
      title: "Client",
      width: 160,
      sorter: true,
      sortOrder: columnSortOrder("client"),
      render: (_, record) => (
        <Flex vertical gap={2}>
          <button
            type="button"
            className={styles.clientNameLink}
            onClick={() => onViewBooking?.(record)}
          >
            {record.name}
          </button>
          <Text variant="caption" size="md" color="var(--color-text-secondary-dark)">
            {record.bookingId}
          </Text>
        </Flex>
      )
    },
    {
      key: "contact",
      title: "Client Contact",
      render: (_, record) => (
        <Flex vertical gap={2}>
          {record.phone && (
            <Flex align="center" gap={6}>
              <PhoneOutlined className={styles.contactIcon} />
              <Text variant="caption" size="md" color="var(--color-text-primary)">{record.phone}</Text>
            </Flex>
          )}
          {record.email && (
            <Flex align="center" gap={6}>
              <MailOutlined className={styles.contactIcon} />
              <Text variant="caption" size="md" color="var(--color-text-primary)">{record.email}</Text>
            </Flex>
          )}
        </Flex>
      )
    },
    {
      key: "experience",
      title: "Experience",
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <TicketTypeIcon isGroup={!!record.experienceIsGroup} />
          <Flex vertical gap={2}>
            <Text variant="body" size="md" fw={500} color="var(--color-text-primary)" className={styles.experienceTitle} title={record.experience}>
              {record.experience}
            </Text>
            <Text variant="caption" size="md" color="var(--color-text-secondary-dark)">{record.experienceDate}</Text>
            {record.experienceTime && (
              <Text variant="caption" size="md" color="var(--color-text-secondary-dark)">{record.experienceTime}</Text>
            )}
          </Flex>
        </Flex>
      )
    },
    {
      key: "guests",
      title: "Guests",
      width: 80,
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <UserOutlined className={styles.guestIcon} />
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">{record.guestCount}</Text>
        </Flex>
      )
    },
    {
      key: "payment",
      dataIndex: "payment",
      width: 180,
      title: "Payment",
      sorter: true,
      sortOrder: columnSortOrder("payment"),
      render: (_, record) => (
        <Flex vertical align="flex-start" gap={4} className="w-max">
          {record.paymentTag && (
            <PaymentTag label={record.paymentTag.label} color={record.paymentTag.color} />
          )}
          {record.collectionTag && (
            <PaymentTag label={record.collectionTag.label} color={record.collectionTag.color} />
          )}
        </Flex>
      )
    },
    {
      key: "waivers",
      dataIndex: "waivers",
      title: "Waivers / Docs",
      width: 140,
      sorter: true,
      sortOrder: columnSortOrder("waivers"),
      render: (_, record) => (
        <WaiverCell docsSign={record.docsSign ?? 0} docsTotal={record.docsTotal ?? 0} />
      )
    },
    {
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => onViewBooking?.(record)}>
          View
        </Button>
      )
    }
  ]

  // Requests tab (Figma): no In/Out or Waivers columns, and the row action is Accept/Decline
  // instead of View — the guide approves or declines the pending request inline.
  const requestActionsColumn = {
    key: "requestActions",
    width: 200,
    render: (_, record) => (
      <Flex gap={8} justify="flex-end">
        <Button color="primary" variant="outlined" shape="round" size="small" onClick={() => openRequestConfirm(record, "decline")}>
          Decline
        </Button>
        <Button type="primary" shape="round" size="small" onClick={() => openRequestConfirm(record, "accept")}>
          Accept
        </Button>
      </Flex>
    )
  }

  const visibleColumns = isRequestsTab
    ? columns
        .filter((column) => !["inOut", "waivers", "actions"].includes(column.key))
        .concat(requestActionsColumn)
    : columns

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    columnWidth: 40
  }

  return (
    <Flex vertical className={styles.container}>
      {activeChips.length > 0 && (
        <Flex align="center" gap={8} wrap="wrap" className={styles.filterChipsRow}>
          {activeChips.map((chip) => (
            <FilterChip key={chip.key} label={chip.label} onRemove={() => removeChip(chip.key)} />
          ))}
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">
            {metadata?.total_results ?? reservations.length} results
          </Text>
        </Flex>
      )}

      {selectedRowKeys.length > 0 && (
        <Flex align="center" gap={8} className={styles.selectionBar}>
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">{selectedRowKeys.length} selected</Text>
          <SendMessageButton
            recipients={selectedReservations}
            label="Message Clients"
            buttonProps={{ type: "primary", size: "small", shape: "round" }}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <button
            type="button"
            className={styles.clearSelectionBtn}
            onClick={() => setSelectedRowKeys([])}
            aria-label="Clear selection"
          >
            <CloseOutlined />
          </button>
        </Flex>
      )}

      <Table
        columns={visibleColumns}
        dataSource={data}
        loading={loading}
        rowSelection={rowSelection}
        rowKey="key"
        pagination={false}
        onChange={onSortChange}
        scroll={{ x: "max-content" }}
      />

      <CustomPagination
        metadata={metadata || {
          page,
          total_pages: Math.ceil((reservations.length || 0) / pageSize) || 1,
          next_page: null,
          prev_page: null
        }}
        pageSize={pageSize}
        total={metadata?.total_results ?? reservations.length}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[15, 25, 50, 100]}
      />

      <ReservationFilterModal
        open={filterModalOpen}
        filters={filters}
        onApply={applyFilters}
        onClear={clearAllFilters}
        onClose={() => setFilterModalOpen(false)}
      />

      <IncompleteDocumentsModal
        open={!!checkIn.incompleteItem}
        onClose={checkIn.closeIncomplete}
        onOverride={checkIn.handleOverride}
      />

      <CheckInGuestsModal
        open={!!checkIn.checkInItem}
        onClose={checkIn.closeCheckIn}
        bookingId={checkIn.checkInItem?.bookingId}
        clientName={checkIn.checkInItem?.name}
        guestTypes={checkIn.checkInItem?.guestTypes}
        onConfirm={checkIn.handleConfirmCheckIn}
      />

      <CheckOutGuestsModal
        open={!!checkIn.checkOutItem}
        onClose={checkIn.closeCheckOut}
        bookingId={checkIn.checkOutItem?.bookingId}
        clientName={checkIn.checkOutItem?.name}
        guestTypes={checkIn.checkOutItem?.guestTypes}
        onConfirm={checkIn.handleConfirmCheckOut}
      />

      {confirmTarget && (
        <BookingConfirmationModal
          open={!!confirmTarget}
          bookingConfirmStatus={confirmTarget.action}
          clientName={confirmTarget.record.name}
          tripDate={confirmTarget.record.experienceDate}
          handleCancel={closeRequestConfirm}
          handleConfirm={handleConfirmRequest}
        />
      )}
    </Flex>
  )
}

ReservationListingTable.propTypes = {
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      bookingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      experience: PropTypes.string,
      experienceDate: PropTypes.string,
      experienceTime: PropTypes.string,
      guestCount: PropTypes.number,
      checkInState: PropTypes.oneOf(["pending", "in", "out"]),
      paymentTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      collectionTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      docsSign: PropTypes.number,
      docsTotal: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  status: PropTypes.string,
  onRefresh: PropTypes.func,
  metadata: PropTypes.object,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.string,
  onSortChange: PropTypes.func,
  onViewBooking: PropTypes.func,
  onOpenFilters: PropTypes.shape({ current: PropTypes.func }),
  filters: PropTypes.shape({
    payment: PropTypes.string,
    waivers: PropTypes.string,
    checkIn: PropTypes.string,
    experiences: PropTypes.arrayOf(PropTypes.number)
  }),
  onFiltersChange: PropTypes.func,
  experienceOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }))
}

export default ReservationListingTable
